import { useMemo } from "react";
import type { Container, ContainerStatus, RiskLevel, DocumentStatus } from "@/types/container";
import { generateRealisticRoute, getPortCoordinates } from "@/utils/route-generator";
import { parseUtcISO } from "@/utils/date-formatters";
import type { Waypoint } from "@/components/application/map/route-map";
import { useContainerTable } from "@/hooks/api/use-containers-recap";
import { ROUTE_COLORS, clamp, type WorldMapRoute } from "./world-map-utils";

export type DeliveryFilter = "all" | "in_progress" | "delivered";

export interface UseWorldMapDataParams {
    searchTerm: string;
    /** Filter by delivery status: in_progress (default), delivered, or all */
    deliveryFilter?: DeliveryFilter;
}

export function useWorldMapData({ searchTerm, deliveryFilter = "in_progress" }: UseWorldMapDataParams) {
    const { data: tableData } = useContainerTable({
        startDate: null,
        endDate: null,
        skip: 0,
        limit: 100,
        includeSeaRoute: true,
    });

    const containers: Container[] = useMemo(() => {
        if (!tableData?.containers) return [];

        return tableData.containers.map((row): Container => {
            const mapStatus = (apiStatus: string): ContainerStatus => {
                const s = apiStatus?.toLowerCase() || '';
                const statusMap: Record<string, ContainerStatus> = {
                    'draft': 'Draft',
                    'booked': 'Booked',
                    'in_transit': 'In Transit',
                    'arrived': 'Arrived',
                    'delivered': 'Delivered',
                    'on_hold': 'On Hold',
                    'cancelled': 'Cancelled',
                    'booking_confirmed': 'Booked',
                    'documents_pending': 'Booked',
                    'ready_to_ship': 'Booked',
                    'arrived_at_port': 'Arrived',
                    'customs_clearance': 'Arrived',
                    'customs_cleared': 'Arrived',
                    'out_for_delivery': 'Delivered',
                    'completed': 'Delivered',
                };
                return statusMap[s] || 'Draft';
            };

            const mapRiskLevel = (apiRisk: string): RiskLevel => {
                const r = apiRisk?.toLowerCase() || '';
                if (r === 'low') return 'Low';
                if (r === 'medium') return 'Medium';
                if (r === 'high') return 'High';
                if (r === 'critical') return 'Critical';
                return 'Low';
            };

            const mapDocStatus = (): DocumentStatus => {
                if (row.documents_complete && row.documents_validated) return 'Completo';
                if (row.documents_complete && !row.documents_validated) return 'Error';
                return 'Pending';
            };

            const countryCodeToName: Record<string, string> = {
                'US': 'USA', 'IT': 'Italy', 'NL': 'Netherlands', 'DE': 'Germany',
                'FR': 'France', 'GB': 'UK', 'ES': 'Spain', 'CN': 'China',
                'JP': 'Japan', 'SG': 'Singapore', 'AE': 'UAE', 'IN': 'India',
                'VN': 'Vietnam', 'TH': 'Thailand', 'BD': 'Bangladesh', 'PH': 'Philippines',
                'AR': 'Argentina', 'BR': 'Brazil', 'PE': 'Peru', 'EC': 'Ecuador',
                'CO': 'Colombia', 'CL': 'Chile', 'ZA': 'South Africa', 'KE': 'Kenya',
                'GH': 'Ghana', 'PA': 'Panama', 'CA': 'Canada',
            };

            const originCountry = row.origin_country_code
                ? countryCodeToName[row.origin_country_code] || row.origin_country_code
                : '';
            const destCountry = row.destination_country_code
                ? countryCodeToName[row.destination_country_code] || row.destination_country_code
                : '';

            const routeFrom = row.origin_port_name
                ? `${row.origin_port_name}${originCountry ? `, ${originCountry}` : ''}`
                : '-';
            const routeTo = row.destination_port_name
                ? `${row.destination_port_name}${destCountry ? `, ${destCountry}` : ''}`
                : '-';

            const waypoints: Waypoint[] =
                row.sea_route?.coordinates && row.sea_route.coordinates.length > 0
                    ? row.sea_route.coordinates.map((coord, i) => {
                          const len = row.sea_route!.coordinates.length;
                          return {
                              id: `wp-${i}`,
                              lat: coord[0],
                              lng: coord[1],
                              type: i === 0 ? ("start" as const) : i === len - 1 ? ("end" as const) : ("stop" as const),
                          };
                      })
                    : generateRealisticRoute(
                          routeFrom,
                          routeTo,
                          row.created_at ? new Date(row.created_at) : new Date()
                      );

            return {
                id: row.container_id,
                shipperId: '',
                receiverId: row.consignee_company_id || '',
                containerNumber: row.container_number,
                route: { from: routeFrom, to: routeTo },
                progress: row.progress_percentage,
                eta: row.estimated_arrival_date ? new Date(row.estimated_arrival_date) : new Date(),
                documentStatus: mapDocStatus(),
                riskLevel: mapRiskLevel(row.risk_level),
                value: {
                    amount: row.total_value_usd || row.freight_charges_usd || 0,
                    currency: row.currency || 'USD'
                },
                status: mapStatus(row.status),
                shippingCompany: row.carrier_name || undefined,
                commodity: 'avocado',
                createdAt: row.created_at ? new Date(row.created_at) : new Date(),
                updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),
                waypoints,
                bookingNumber: row.booking_number || undefined,
                vesselName: row.vessel_name || undefined,
                voyageNumber: row.voyage_number || undefined,
                consigneeName: row.consignee_name || undefined,
            };
        });
    }, [tableData]);

    const filteredContainers = useMemo(() => {
        let list = containers;
        if (deliveryFilter === "in_progress") {
            list = list.filter((c) => c.status !== "Delivered");
        } else if (deliveryFilter === "delivered") {
            list = list.filter((c) => c.status === "Delivered");
        }
        if (!searchTerm.trim()) return list;
        const term = searchTerm.toLowerCase();
        return list.filter(c =>
            (c.containerNumber && c.containerNumber.toLowerCase().includes(term)) ||
            (c.bookingNumber && c.bookingNumber.toLowerCase().includes(term)) ||
            (c.shippingCompany && c.shippingCompany.toLowerCase().includes(term)) ||
            (c.vesselName && c.vesselName.toLowerCase().includes(term)) ||
            (c.route.from && c.route.from.toLowerCase().includes(term)) ||
            (c.route.to && c.route.to.toLowerCase().includes(term)) ||
            (c.consigneeName && c.consigneeName.toLowerCase().includes(term))
        );
    }, [containers, searchTerm, deliveryFilter]);

    const progressById = useMemo(() => {
        const progress: Record<string, number> = {};
        for (const c of filteredContainers) {
            progress[c.id] = clamp((c.progress ?? 0) / 100, 0, 0.99);
        }
        return progress;
    }, [filteredContainers]);

    const lastUpdateTime = useMemo(() => {
        if (!tableData?.containers || tableData.containers.length === 0) return null;
        const trackedDates = tableData.containers
            .filter(c => c.last_tracked_at)
            .map(c => parseUtcISO(c.last_tracked_at!)?.getTime())
            .filter((t): t is number => t != null);
        if (trackedDates.length === 0) return null;
        return new Date(Math.max(...trackedDates));
    }, [tableData?.containers]);

    const routes = useMemo(() => {
        const result = filteredContainers
            .map((c, idx) => {
                const waypoints =
                    c.waypoints && c.waypoints.length > 0
                        ? c.waypoints
                        : generateRealisticRoute(c.route.from, c.route.to, c.createdAt);

                if (waypoints.length === 0) {
                    if (import.meta.env.DEV) {
                        console.warn("[Live Tracking] Container excluded (no waypoints):", c.id, c.containerNumber, c.route);
                    }
                    return null;
                }

                const coordinates = waypoints.map((w) => [w.lat, w.lng] as [number, number]);
                const fromCoord = coordinates[0];
                const toCoord = coordinates[coordinates.length - 1];

                if (!fromCoord || !toCoord) {
                    if (import.meta.env.DEV) {
                        console.warn("[Live Tracking] Container excluded (missing from/to):", c.id, c.containerNumber);
                    }
                    return null;
                }

                let finalWaypoints = waypoints;
                let finalCoordinates = coordinates;
                const hasValidCoordinates = coordinates.every(([lat, lng]) => lat !== 0 || lng !== 0);
                if (!hasValidCoordinates) {
                    // Fallback: try to resolve origin/destination so the container still appears on the map
                    const fromCoords = getPortCoordinates(c.route.from);
                    const toCoords = getPortCoordinates(c.route.to);
                    if (fromCoords && toCoords) {
                        finalWaypoints = [
                            { id: "start", lat: fromCoords.lat, lng: fromCoords.lng, label: c.route.from, type: "start" as const },
                            { id: "end", lat: toCoords.lat, lng: toCoords.lng, label: c.route.to, type: "end" as const },
                        ];
                        finalCoordinates = [
                            [fromCoords.lat, fromCoords.lng],
                            [toCoords.lat, toCoords.lng],
                        ];
                    } else {
                        if (import.meta.env.DEV) {
                            console.warn(
                                "[Live Tracking] Container excluded (invalid coordinates 0,0 - port names may not match MAJOR_PORTS):",
                                c.id,
                                c.containerNumber,
                                c.route
                            );
                        }
                        return null;
                    }
                }

                const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
                return {
                    c,
                    waypoints: finalWaypoints,
                    coordinates: finalCoordinates,
                    from: finalCoordinates[0],
                    to: finalCoordinates[finalCoordinates.length - 1],
                    color,
                } as const;
            })
            .filter(Boolean) as WorldMapRoute[];

        if (import.meta.env.DEV && filteredContainers.length > 0 && result.length < filteredContainers.length) {
            console.info(
                `[Live Tracking] Showing ${result.length} of ${filteredContainers.length} containers on map (${filteredContainers.length - result.length} excluded - check warnings above)`
            );
        }
        return result;
    }, [filteredContainers]);

    const allPoints = useMemo(() => routes.flatMap((r) => r.coordinates), [routes]);

    return {
        containers,
        filteredContainers,
        routes,
        progressById,
        lastUpdateTime,
        allPoints,
    };
}
